import { Link } from 'gatsby';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { PlaybackItem, RecordingPreviewData } from '../types';
import { cssClampValue, recordingToPlaybackItem, secondsToHhMmSs } from '../utils/common';
import { frontendHost } from '../../config';
import RecordingPlayPauseButton from './RecordingPlayPauseButton';
import AnimatedRecordistsList from './AnimatedRecordistsList';
import DefaultRecordingImage from './DefaultRecordingImage';

const playPauseButtonSize = { mobile: 26, desktop: 50 };

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: ${cssClampValue(133, 257)};
  height: ${cssClampValue(133, 257)};
  padding: ${cssClampValue(15, 30)};
  flex-shrink: 0;
  font-size: ${cssClampValue(9.4, 18)};
  background: var(--earth-fm--color--black);
  color: var(--earth-fm--color--white);
  border-radius: 10px;
  overflow: hidden;
  isolation: isolate; /* fixes overflow hidden issue with border-radius on Safari */
`;

const Cover = styled.div`
  a {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 31.77%, #000000 100%);
    pointer-events: none;
  }
`;

const Content = styled.div`
  position: relative;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  a {
    text-decoration: none;
    color: inherit;

    &:hover,
    &:focus {
      color: inherit;
    }
  }
`;

const Title = styled.h3`
  margin: ${cssClampValue(15, 30)} 0 0;
  font-family: inherit;
  font-size: ${cssClampValue(10.4, 20)};
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  &:focus-within {
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
  }

  a {
    text-decoration: none;

    &:focus {
      outline: 0;
    }
  }
`;

const Duration = styled.div<{ $isLive: boolean }>`
  line-height: 1.27;

  ${({ $isLive }) =>
    $isLive
      ? css`
          display: inline-block;
          padding: 0 12px;
          font-size: 13px;
          line-height: 20px;
          font-weight: bold;
          text-transform: uppercase;
          color: var(--earth-fm--color--green);
          background: var(--earth-fm--color--white);
          border-radius: 4px;
        `
      : null}
`;

export interface RecordingCardAltProps {
  recording: RecordingPreviewData;
  className?: string;
  titleAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div';
}

export default function RecordingCardAlt({
  recording,
  className = '',
  titleAs = 'h3',
}: RecordingCardAltProps) {
  const [isMouseOver, setIsMouseOver] = React.useState(false);
  const playbackItem: PlaybackItem | null = React.useMemo(
    () => recordingToPlaybackItem(recording),
    [recording],
  );

  const image = recording.featuredImage ? getImage(recording.featuredImage) : null;

  if (!recording.uri || !recording.title || !recording.recordists) {
    return null;
  }

  return (
    <Wrapper
      className={className}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      <Cover className={`${className}__cover`}>
        <Link to={recording.uri} tabIndex={-1}>
          {image ? (
            <GatsbyImage
              image={image}
              alt={`${recording.title} - nature soundscape - ${frontendHost}`}
            />
          ) : (
            <DefaultRecordingImage alt={recording.title} />
          )}
        </Link>
      </Cover>
      <Content className={`${className}__content`}>
        {playbackItem ? (
          <RecordingPlayPauseButton
            playbackItem={playbackItem}
            size={playPauseButtonSize}
            renderSource="recording_card"
          />
        ) : null}
        <Title className={`${className}__title`} as={titleAs}>
          <Link to={recording.uri}>{recording.userFriendlyTitle || recording.title}</Link>
        </Title>
        <AnimatedRecordistsList recordists={recording.recordists} isSliding={isMouseOver} />
        <Duration $isLive={recording.isLivestream || false}>
          {recording.isLivestream ? 'LIVE' : secondsToHhMmSs(recording.duration)}
        </Duration>
      </Content>
    </Wrapper>
  );
}
